<template>
  <div class="email-tpl">
    <APageHead title="이메일 템플릿 관리" sub="이메일 발송에 사용되는 HTML 템플릿을 관리합니다.">
      <template #actions>
        <ABtn kind="ghost" icon="refresh" @click="loadTemplates">새로고침</ABtn>
        <ABtn kind="yellow" icon="add" @click="openCreateModal">새 템플릿</ABtn>
      </template>
    </APageHead>

    <div class="email-tpl__layout">
      <!-- 좌측 템플릿 목록 -->
      <ACard body-flush>
        <div class="email-tpl__list-head">
          <h5 class="email-tpl__list-h">
            <i class="material-icons-round">folder</i>
            템플릿 목록
            <APill kind="yellow">{{ filteredTemplates.length }}</APill>
          </h5>
        </div>

        <div class="email-tpl__lang-tabs">
          <button
            v-for="lang in languageTabs"
            :key="lang.code"
            type="button"
            class="email-tpl__lang-tab"
            :class="{ 'is-active': selectedLanguage === lang.code }"
            @click="selectedLanguage = lang.code"
          >
            <span v-if="lang.flag">{{ lang.flag }}</span>
            {{ lang.label }}
            <span class="email-tpl__lang-count">{{ getTemplateCountByLanguage(lang.code) }}</span>
          </button>
        </div>

        <AEmpty
          v-if="!loadingList && filteredTemplates.length === 0"
          title="템플릿이 없습니다"
          :sub="selectedLanguage === 'all' ? '새 템플릿을 생성해주세요' : '해당 언어의 템플릿이 없습니다'"
        >
          <template #action>
            <ABtn kind="yellow" icon="add" @click="openCreateModal">새 템플릿 만들기</ABtn>
          </template>
        </AEmpty>

        <div v-else class="email-tpl__list">
          <button
            v-for="template in filteredTemplates"
            :key="template.name"
            type="button"
            class="email-tpl__item"
            :class="{ 'is-active': selectedTemplate && selectedTemplate.name === template.name }"
            @click="selectTemplate(template)"
          >
            <div class="email-tpl__item-top">
              <i class="material-icons-round email-tpl__item-icon">description</i>
              <div class="email-tpl__item-main">
                <h6 class="email-tpl__item-name">{{ template.name }}</h6>
                <p class="email-tpl__item-file">{{ template.filename }}</p>
              </div>
              <span
                class="email-tpl__item-del"
                title="삭제"
                @click.stop="confirmDelete(template)"
              >
                <i class="material-icons-round">delete</i>
              </span>
            </div>
            <div class="email-tpl__item-meta">
              <APill :kind="langPillKind(getTemplateLanguage(template.name))">
                {{ getLanguageLabel(getTemplateLanguage(template.name)) }}
              </APill>
              <span class="email-tpl__meta-tag">
                <i class="material-icons-round">straighten</i>
                {{ formatFileSize(template.size) }}
              </span>
              <span class="email-tpl__meta-tag">
                <i class="material-icons-round">schedule</i>
                {{ formatDate(template.modified_at) }}
              </span>
            </div>
          </button>
        </div>
      </ACard>

      <!-- 우측 에디터 -->
      <ACard body-flush>
        <div class="email-tpl__editor-head">
          <h5 class="email-tpl__list-h">
            <i class="material-icons-round">code</i>
            {{ selectedTemplate ? selectedTemplate.name : '템플릿 편집기' }}
          </h5>
          <div v-if="selectedTemplate" class="email-tpl__editor-actions">
            <ABtn kind="ghost" icon="visibility" size="sm" @click="openPreviewModal">미리보기</ABtn>
            <ABtn kind="yellow" icon="save" size="sm" :disabled="saving" :loading="saving" @click="saveTemplate">
              {{ saving ? '저장 중' : '저장' }}
            </ABtn>
          </div>
        </div>

        <div class="email-tpl__editor-body">
          <AEmpty
            v-if="!selectedTemplate"
            title="템플릿을 선택해주세요"
            sub="왼쪽 목록에서 편집할 템플릿을 선택하세요"
          />

          <div v-else>
            <div class="email-tpl__var-helper">
              <span class="email-tpl__var-label">사용 가능한 변수:</span>
              <APill kind="info" class="email-tpl__var-chip" @click="insertVariable('title')" v-text="'{{ title }}'" />
              <APill kind="info" class="email-tpl__var-chip" @click="insertVariable('body')" v-text="'{{ body }}'" />
              <APill kind="yellow" class="email-tpl__var-chip" @click="insertVariable('body | safe')" v-text="'{{ body | safe }}'" />
            </div>

            <div class="email-tpl__editor-wrap">
              <MonacoEditor
                :value="editContent"
                language="html"
                theme="vs-dark"
                :options="editorOptions"
                @change="onEditorChange"
                @mounted="onEditorMounted"
                style="height: 520px;"
              />
            </div>

            <div class="email-tpl__editor-foot">
              <span class="email-tpl__editor-hint">
                <i class="material-icons-round">info</i>
                Jinja2 템플릿 엔진 사용 — HTML body에 safe 필터 권장
              </span>
              <span v-if="hasChanges" class="email-tpl__editor-warn">
                <i class="material-icons-round">warning</i>
                저장되지 않은 변경사항이 있습니다
              </span>
            </div>
          </div>
        </div>
      </ACard>
    </div>

    <!-- 새 템플릿 모달 -->
    <AModal v-model="createOpen" title="새 템플릿 생성" size="md">
      <div class="p-form-grid">
        <div class="p-field">
          <label class="p-field__label">템플릿 이름 <span class="p-field__req">*</span></label>
          <input
            v-model="newTemplate.name"
            class="p-input"
            :class="{ 'is-invalid': !!nameError }"
            placeholder="예: welcome_email"
            @input="validateTemplateName"
          />
          <p class="p-field__hint">영문, 숫자, 언더스코어(_), 하이픈(-)만 사용 가능</p>
          <p v-if="nameError" class="p-field__error">{{ nameError }}</p>
        </div>
        <div class="p-field">
          <label class="p-check">
            <input v-model="useDefaultTemplate" type="checkbox" />
            <span>기본 템플릿 사용</span>
          </label>
        </div>
        <div v-if="!useDefaultTemplate" class="p-field">
          <label class="p-field__label">초기 내용</label>
          <textarea
            v-model="newTemplate.content"
            class="p-input p-input--area"
            rows="10"
            placeholder="HTML 내용을 입력하세요..."
          />
        </div>
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="createOpen = false">취소</ABtn>
        <ABtn
          kind="yellow"
          icon="check"
          :disabled="!newTemplate.name || !!nameError || creating"
          :loading="creating"
          @click="createTemplate"
        >{{ creating ? '생성 중' : '생성' }}</ABtn>
      </template>
    </AModal>

    <!-- 삭제 확인 모달 -->
    <AModal v-model="deleteOpen" title="템플릿 삭제" size="sm">
      <div class="email-tpl__delete-confirm">
        <i class="material-icons-round email-tpl__delete-icon">warning</i>
        <h6>정말 삭제하시겠습니까?</h6>
        <p class="email-tpl__delete-name">
          템플릿 <strong>{{ templateToDelete?.name }}</strong>을(를) 삭제합니다.
        </p>
        <p class="email-tpl__delete-warn">삭제된 템플릿은 복구할 수 없습니다.</p>
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="deleteOpen = false">취소</ABtn>
        <ABtn
          kind="danger-fill"
          icon="delete"
          :disabled="deleting"
          :loading="deleting"
          @click="deleteTemplate"
        >{{ deleting ? '삭제 중' : '삭제' }}</ABtn>
      </template>
    </AModal>

    <!-- 미리보기 모달 -->
    <AModal v-model="previewOpen" title="템플릿 미리보기" size="xl">
      <div class="email-tpl__preview-grid">
        <div class="email-tpl__preview-form">
          <h6 class="email-tpl__preview-h">미리보기 데이터</h6>
          <div class="p-field">
            <label class="p-field__label">제목 (title)</label>
            <input v-model="previewData.title" class="p-input" placeholder="이메일 제목" />
          </div>
          <div class="p-field">
            <label class="p-field__label">본문 (body)</label>
            <textarea
              v-model="previewData.body"
              class="p-input p-input--area"
              rows="8"
              placeholder="이메일 본문 HTML..."
            />
          </div>
          <ABtn kind="yellow" icon="refresh" block :disabled="loadingPreview" :loading="loadingPreview" @click="generatePreview">
            {{ loadingPreview ? '생성 중' : '미리보기 생성' }}
          </ABtn>
        </div>
        <div class="email-tpl__preview-result">
          <h6 class="email-tpl__preview-h">렌더링 결과</h6>
          <div v-if="previewError" class="email-tpl__preview-err">{{ previewError }}</div>
          <iframe
            v-else
            ref="previewIframe"
            :srcdoc="previewHtml"
            class="email-tpl__preview-iframe"
          />
        </div>
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="previewOpen = false">닫기</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import MonacoEditor from "@guolao/vue-monaco-editor";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";

export default {
  name: "SystemEmailMassages",
  components: { MonacoEditor, APageHead, ACard, ABtn, APill, AModal, AEmpty },
  data() {
    return {
      templates: [],
      loadingList: false,
      selectedLanguage: "all",
      languageTabs: [
        { code: "all",   label: "전체",     flag: null },
        { code: "ko",    label: "한국어",   flag: "🇰🇷" },
        { code: "en",    label: "English",  flag: "🇺🇸" },
        { code: "ja",    label: "日本語",   flag: "🇯🇵" },
        { code: "zh",    label: "中文",     flag: "🇨🇳" },
        { code: "es",    label: "Español",  flag: "🇪🇸" },
        { code: "other", label: "기타",     flag: null },
      ],
      selectedTemplate: null,
      editContent: "",
      originalContent: "",
      loadingDetail: false,
      saving: false,
      createOpen: false,
      newTemplate: { name: "", content: "" },
      useDefaultTemplate: true,
      nameError: "",
      creating: false,
      deleteOpen: false,
      templateToDelete: null,
      deleting: false,
      previewOpen: false,
      previewData: {
        title: "테스트 이메일 제목",
        body: "<p>안녕하세요!</p><p>이메일 본문 <strong>HTML</strong> 내용입니다.</p>",
      },
      previewHtml: "",
      loadingPreview: false,
      previewError: "",
      editorInstance: null,
      editorOptions: {
        minimap: { enabled: false },
        fontSize: 14,
        lineNumbers: "on",
        wordWrap: "on",
        automaticLayout: true,
        scrollBeyondLastLine: false,
        tabSize: 2,
        theme: "vs-dark",
      },
      defaultTemplateContent: `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>{{ title }}</title>
    <style>
        body { font-family: 'Pretendard', sans-serif; line-height: 1.6; color: #1A1612; max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: #1A1612; color: #FFC93C; padding: 24px; text-align: center; border-radius: 12px 12px 0 0; }
        .content { background: #ffffff; padding: 30px; border: 1px solid #E6DCC4; border-top: none; }
        .footer { background: #F4EFE0; padding: 16px; text-align: center; font-size: 12px; color: #6B6359; border-radius: 0 0 12px 12px; }
    </style>
</head>
<body>
    <div class="header"><h1>{{ title }}</h1></div>
    <div class="content">{{ body | safe }}</div>
    <div class="footer">
        <p>본 메일은 발신 전용입니다.</p>
        <p>&copy; 2024 Pineapple. All rights reserved.</p>
    </div>
</body>
</html>`,
    };
  },
  computed: {
    hasChanges() {
      return this.editContent !== this.originalContent;
    },
    filteredTemplates() {
      if (this.selectedLanguage === "all") return this.templates;
      return this.templates.filter((t) => {
        const lang = this.getTemplateLanguage(t.name);
        if (this.selectedLanguage === "other") {
          const known = ["ko", "en", "ja", "zh", "es"];
          return !known.includes(lang);
        }
        return lang === this.selectedLanguage;
      });
    },
  },
  mounted() {
    this.loadTemplates();
  },
  methods: {
    async loadTemplates() {
      this.loadingList = true;
      try {
        const response = await this.$axios({
          method: "get",
          url: "/system/email-templates",
          loadingBar: true,
        });
        this.templates = response.data.items || [];
      } catch (error) {
        console.error("템플릿 목록 조회 실패:", error);
        this.$notifyVue({ type: "danger", title: "템플릿 목록을 불러오는데 실패했습니다." });
      } finally {
        this.loadingList = false;
      }
    },

    async selectTemplate(template) {
      if (this.hasChanges) {
        const confirmed = confirm("저장되지 않은 변경사항이 있습니다. 계속하시겠습니까?");
        if (!confirmed) return;
      }
      this.selectedTemplate = template;
      this.loadingDetail = true;
      try {
        const response = await this.$axios({
          method: "get",
          url: `/system/email-templates/${template.name}`,
          loadingBar: true,
        });
        this.editContent = response.data.content || "";
        this.originalContent = this.editContent;
        if (this.editorInstance) this.editorInstance.setValue(this.editContent);
      } catch (error) {
        console.error("템플릿 상세 조회 실패:", error);
        this.$notifyVue({ type: "danger", title: "템플릿을 불러오는데 실패했습니다." });
        this.selectedTemplate = null;
      } finally {
        this.loadingDetail = false;
      }
    },

    async saveTemplate() {
      if (!this.selectedTemplate) return;
      this.saving = true;
      try {
        await this.$axios({
          method: "put",
          url: `/system/email-templates/${this.selectedTemplate.name}`,
          data: { content: this.editContent },
          loadingBar: true,
        });
        this.originalContent = this.editContent;
        this.$notifyVue({ type: "success", title: "템플릿이 저장되었습니다." });
        this.loadTemplates();
      } catch (error) {
        console.error("템플릿 저장 실패:", error);
        const message = error.response?.data?.detail || "템플릿 저장에 실패했습니다.";
        this.$notifyVue({ type: "danger", title: message });
      } finally {
        this.saving = false;
      }
    },

    openCreateModal() {
      this.newTemplate = { name: "", content: "" };
      this.useDefaultTemplate = true;
      this.nameError = "";
      this.createOpen = true;
    },

    validateTemplateName() {
      const name = this.newTemplate.name;
      if (!name) { this.nameError = ""; return; }
      const validPattern = /^[a-zA-Z0-9_-]+$/;
      if (!validPattern.test(name)) {
        this.nameError = "영문, 숫자, 언더스코어(_), 하이픈(-)만 사용 가능합니다.";
        return;
      }
      if (this.templates.some((t) => t.name === name)) {
        this.nameError = "이미 존재하는 템플릿 이름입니다.";
        return;
      }
      this.nameError = "";
    },

    async createTemplate() {
      if (!this.newTemplate.name || this.nameError) return;
      this.creating = true;
      try {
        const content = this.useDefaultTemplate
          ? this.defaultTemplateContent
          : this.newTemplate.content || this.defaultTemplateContent;

        await this.$axios({
          method: "post",
          url: "/system/email-templates",
          data: { name: this.newTemplate.name, content },
          loadingBar: true,
        });

        this.$notifyVue({ type: "success", title: "템플릿이 생성되었습니다." });
        this.createOpen = false;
        await this.loadTemplates();

        const created = this.templates.find((t) => t.name === this.newTemplate.name);
        if (created) this.selectTemplate(created);
      } catch (error) {
        console.error("템플릿 생성 실패:", error);
        const message = error.response?.data?.detail || "템플릿 생성에 실패했습니다.";
        this.$notifyVue({ type: "danger", title: message });
      } finally {
        this.creating = false;
      }
    },

    confirmDelete(template) {
      this.templateToDelete = template;
      this.deleteOpen = true;
    },

    async deleteTemplate() {
      if (!this.templateToDelete) return;
      this.deleting = true;
      try {
        await this.$axios({
          method: "delete",
          url: `/system/email-templates/${this.templateToDelete.name}`,
          loadingBar: true,
        });
        this.$notifyVue({ type: "success", title: "템플릿이 삭제되었습니다." });

        if (this.selectedTemplate?.name === this.templateToDelete.name) {
          this.selectedTemplate = null;
          this.editContent = "";
          this.originalContent = "";
        }
        this.deleteOpen = false;
        this.loadTemplates();
      } catch (error) {
        console.error("템플릿 삭제 실패:", error);
        const message = error.response?.data?.detail || "템플릿 삭제에 실패했습니다.";
        this.$notifyVue({ type: "danger", title: message });
      } finally {
        this.deleting = false;
        this.templateToDelete = null;
      }
    },

    openPreviewModal() {
      this.previewHtml = "";
      this.previewError = "";
      this.previewOpen = true;
    },

    async generatePreview() {
      if (!this.selectedTemplate) return;
      this.loadingPreview = true;
      this.previewError = "";
      try {
        const response = await this.$axios({
          method: "post",
          url: `/system/email-templates/${this.selectedTemplate.name}/preview`,
          data: { title: this.previewData.title, body: this.previewData.body },
          responseType: "text",
          loadingBar: true,
        });
        this.previewHtml = response.data;
      } catch (error) {
        console.error("미리보기 생성 실패:", error);
        this.previewError = error.response?.data?.detail || "미리보기 생성에 실패했습니다.";
      } finally {
        this.loadingPreview = false;
      }
    },

    onEditorMounted(editor) {
      this.editorInstance = editor;
      setTimeout(() => editor.layout(), 100);
    },

    onEditorChange(value) {
      this.editContent = value;
    },

    insertVariable(variable) {
      const insertion = `{{ ${variable} }}`;
      if (this.editorInstance) {
        const selection = this.editorInstance.getSelection();
        const range = {
          startLineNumber: selection.startLineNumber,
          startColumn: selection.startColumn,
          endLineNumber: selection.endLineNumber,
          endColumn: selection.endColumn,
        };
        this.editorInstance.executeEdits("", [{ range, text: insertion }]);
        this.editContent = this.editorInstance.getValue();
      } else {
        this.editContent = this.editContent + insertion;
      }
    },

    getTemplateLanguage(name) {
      const match = name.match(/_([a-z]{2})$/i);
      return match ? match[1].toLowerCase() : "other";
    },

    getLanguageLabel(langCode) {
      const langMap = {
        ko: "🇰🇷 한국어",
        en: "🇺🇸 English",
        ja: "🇯🇵 日本語",
        zh: "🇨🇳 中文",
        es: "🇪🇸 Español",
      };
      return langMap[langCode] || "기타";
    },

    langPillKind(langCode) {
      const map = { ko: "info", en: "info", ja: "err", zh: "warn", es: "ok" };
      return map[langCode] || "default";
    },

    getTemplateCountByLanguage(langCode) {
      if (langCode === "all") return this.templates.length;
      return this.templates.filter((t) => {
        const lang = this.getTemplateLanguage(t.name);
        if (langCode === "other") {
          const known = ["ko", "en", "ja", "zh", "es"];
          return !known.includes(lang);
        }
        return lang === langCode;
      }).length;
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 B";
      const units = ["B", "KB", "MB"];
      let i = 0;
      while (bytes >= 1024 && i < units.length - 1) {
        bytes /= 1024;
        i++;
      }
      return `${bytes.toFixed(1)} ${units[i]}`;
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("ko-KR", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.email-tpl {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__layout {
    display: grid;
    grid-template-columns: 380px 1fr;
    gap: 18px;
    align-items: start;

    @media (max-width: 992px) {
      grid-template-columns: 1fr;
    }
  }

  &__list-head {
    padding: 16px 18px 8px;
  }

  &__list-h {
    margin: 0;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    > i { font-size: 18px; color: $p-point-deep; }
  }

  &__lang-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 0 18px 12px;
    border-bottom: 1px solid $p-line;
  }

  &__lang-tab {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border: 1px solid $p-line;
    border-radius: 999px;
    background: $p-panel;
    color: $p-ink-mid;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease, border-color $p-dur $p-ease;

    &:hover {
      background: $p-panel-alt;
      color: $p-ink;
    }

    &.is-active {
      background: $p-ink;
      border-color: $p-ink;
      color: $p-point;
    }
  }

  &__lang-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 16px;
    padding: 0 5px;
    border-radius: 9px;
    background: rgba(0, 0, 0, 0.12);
    font-size: 9px;
    font-weight: 800;
  }

  &__list {
    padding: 12px 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 640px;
    overflow-y: auto;
  }

  &__item {
    text-align: left;
    padding: 12px 14px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    background: $p-panel;
    cursor: pointer;
    transition: border-color $p-dur $p-ease, background $p-dur $p-ease;

    &:hover {
      border-color: $p-point-deep;
      background: $p-point-soft;
    }

    &.is-active {
      border-color: $p-point-deep;
      background: $p-point-soft;
      box-shadow: 0 0 0 2px $p-point-glow;
    }
  }

  &__item-top {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__item-icon {
    color: $p-point-deep;
    font-size: 22px !important;
  }

  &__item-main {
    flex: 1;
    min-width: 0;
  }

  &__item-name {
    margin: 0;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__item-file {
    margin: 2px 0 0;
    font-family: $p-font-mono;
    font-size: 10px;
    color: $p-ink-soft;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__item-del {
    width: 26px;
    height: 26px;
    display: inline-grid;
    place-items: center;
    border-radius: $p-radius;
    color: $p-err;
    cursor: pointer;
    transition: background $p-dur $p-ease;

    > i { font-size: 16px; }

    &:hover { background: $p-err-soft; }
  }

  &__item-meta {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }

  &__meta-tag {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 7px;
    background: $p-panel-alt;
    border-radius: 4px;
    font-size: 10px;
    color: $p-ink-mid;

    > i { font-size: 12px !important; }
  }

  &__editor-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 18px;
    border-bottom: 1px solid $p-line;
  }

  &__editor-actions {
    display: inline-flex;
    gap: 8px;
  }

  &__editor-body {
    padding: 18px;
  }

  &__var-helper {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 12px;
  }

  &__var-label {
    font-size: 11px;
    color: $p-ink-mid;
    font-weight: 700;
  }

  &__var-chip {
    cursor: pointer;
    user-select: none;
  }

  &__editor-wrap {
    border: 1px solid $p-line-hard;
    border-radius: $p-radius-md;
    overflow: hidden;
  }

  &__editor-foot {
    margin-top: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__editor-hint {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: $p-ink-soft;

    > i { font-size: 14px !important; color: $p-info; }
  }

  &__editor-warn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 800;
    color: $p-warn;

    > i { font-size: 14px !important; }
  }

  // ── 삭제 모달 ──
  &__delete-confirm {
    text-align: center;
    padding: 18px 12px;
  }

  &__delete-icon {
    color: $p-err;
    font-size: 56px !important;
    margin-bottom: 12px;
  }

  &__delete-name {
    margin: 8px 0 4px;
    color: $p-ink;
  }

  &__delete-warn {
    color: $p-err;
    font-size: 11px;
    font-weight: 700;
    margin: 0;
  }

  // ── 미리보기 모달 ──
  &__preview-grid {
    display: grid;
    grid-template-columns: 320px 1fr;
    gap: 18px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__preview-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__preview-h {
    margin: 0;
    font-size: 12px;
    font-weight: 800;
    color: $p-ink2;
    padding-bottom: 8px;
    border-bottom: 1px solid $p-line;
  }

  &__preview-result {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__preview-err {
    padding: 12px 14px;
    background: $p-err-soft;
    border-left: 3px solid $p-err;
    border-radius: $p-radius-md;
    color: $p-err;
    font-size: 12px;
  }

  &__preview-iframe {
    width: 100%;
    height: 520px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    background: $p-panel;
  }
}
</style>
